import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { ExtractionActivityService } from '@core/services/ai/extraction-activity.service';

/**
 * Global toolbar marker shown while an AI contract extraction is running.
 *
 * Reads {@link ExtractionActivityService} (root-scoped), so the breathing star
 * stays visible no matter where the user navigates in the app. Clicking it
 * jumps back to the extraction's review screen via the resume deep-link.
 */
@Component({
  selector: 'vex-toolbar-extraction-indicator',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatTooltipModule, TranslatePipe],
  template: `
    @if (tracker.isExtracting()) {
      <button
        type="button"
        class="extraction-indicator"
        [attr.aria-label]="'contracts.add.extractionIndicatorTooltip' | translate"
        [matTooltip]="'contracts.add.extractionIndicatorTooltip' | translate"
        (click)="openExtraction()">
        <span class="ai-pill" aria-hidden="true">
          <mat-icon class="ai-star">auto_awesome</mat-icon>
          @if (tracker.activeCount() > 1) {
            <span class="extraction-count">{{ tracker.activeCount() }}</span>
          }
        </span>
      </button>
    }
  `,
  styles: [`
    :host { display: inline-flex; }

    .extraction-indicator {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      margin: 0;
      border: none;
      background: transparent;
      cursor: pointer;
      line-height: 0;
    }

    /* Highlighted "AI is working" pill — gradient + breathing glow halo. */
    .ai-pill {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      height: 32px;
      padding: 0 13px;
      border-radius: 999px;
      background: linear-gradient(
        135deg,
        rgb(var(--ds-interactive)) 0%,
        rgb(var(--ds-interactive-600)) 100%
      );
      box-shadow: 0 0 0 0 rgb(var(--ds-interactive) / 0.55);
      animation: ai-pill-pulse 1.8s ease-in-out infinite;
      will-change: transform, box-shadow;
    }

    @keyframes ai-pill-pulse {
      0%   { transform: scale(0.97); box-shadow: 0 0 0 0 rgb(var(--ds-interactive) / 0.55); }
      50%  { transform: scale(1.07); box-shadow: 0 0 16px 5px rgb(var(--ds-interactive) / 0.45); }
      100% { transform: scale(0.97); box-shadow: 0 0 0 0 rgb(var(--ds-interactive) / 0); }
    }

    .ai-star {
      display: inline-flex;
      font-size: 18px;
      width: 18px;
      height: 18px;
      color: rgb(var(--ds-surface));
      /* "Gonfle et dégonfle" — breathe with a subtle twinkle rotation. */
      animation: ai-star-breathe 1.8s ease-in-out infinite;
      will-change: transform;
    }

    @keyframes ai-star-breathe {
      0%, 100% { transform: scale(0.9) rotate(-4deg); }
      50%      { transform: scale(1.18) rotate(4deg); }
    }

    .extraction-count {
      min-width: 18px;
      height: 18px;
      padding: 0 5px;
      box-sizing: border-box;
      border-radius: 999px;
      background: rgb(var(--ds-surface) / 0.95);
      color: rgb(var(--ds-interactive-600));
      font-size: 11px;
      font-weight: 800;
      line-height: 18px;
      text-align: center;
    }

    /* Respect reduced-motion: drop the scaling, keep a gentle opacity blink. */
    @media (prefers-reduced-motion: reduce) {
      .ai-pill {
        animation: ai-pill-blink 1.8s ease-in-out infinite;
        transform: none;
        box-shadow: 0 0 10px 2px rgb(var(--ds-interactive) / 0.5);
      }
      .ai-star { animation: none; transform: none; }
      @keyframes ai-pill-blink {
        0%, 100% { opacity: 1; }
        50%      { opacity: 0.6; }
      }
    }
  `]
})
export class ToolbarExtractionIndicatorComponent {
  readonly tracker = inject(ExtractionActivityService);
  private readonly router = inject(Router);

  /** Jump back to the running extraction's review screen (resume deep-link). */
  openExtraction(): void {
    const jobId = this.tracker.latestJobId();
    void this.router.navigate(
      ['/contracts/add'],
      jobId ? { queryParams: { extractionJobId: jobId } } : undefined
    );
  }
}
